/**
 * ``useCaptureOnce`` — fire a catalog event exactly once per component
 * mount, even under React Strict Mode's double-invoke in development.
 *
 * Background
 * ----------
 * Next.js dev mode enables React Strict Mode, which double-invokes
 * ``useEffect`` (including effects with ``[]`` deps) to surface
 * cleanup-related bugs. A pageview-style ``useEffect(() =>
 * captureEvent(...), [])`` therefore fires the event twice in dev.
 * Production builds disable Strict Mode and the effect runs once.
 *
 * The double-fire shows up as inflated counts in PostHog during local
 * walk-throughs and confuses early funnel analysis. This hook adds a
 * ``useRef``-based guard so the catalog event fires exactly once
 * regardless of how many times Strict Mode invokes the effect — same
 * pattern Friedrich documented in PR-6 for ``auth.invite.expired``,
 * promoted here to a single canonical helper.
 *
 * Usage
 * -----
 * Replace pageview-style effects:
 *
 *   useEffect(() => {
 *     captureEvent({ event_name: "marketing.dashboard.viewed", ... });
 *   }, []);
 *
 * with:
 *
 *   useCaptureOnce(() => ({
 *     event_name: "marketing.dashboard.viewed",
 *     ...
 *   }));
 *
 * The builder runs only on the FIRST effect invocation, so any work
 * inside (e.g. ``standardBody()``) also runs only once. The hook is
 * intentionally not parameterised by deps — for events that should
 * re-fire on prop changes (period_changed, mode_switched, etc.),
 * keep using ``useEffect`` with explicit deps and an inline
 * ``captureEvent`` call. ``useCaptureOnce`` is the mount-once tool.
 *
 * Plan anchors
 * ------------
 * - Master decision log entry covering the strict-mode fix.
 * - Plan_05 § "Standardisation audit" — pageview events fire once
 *   per surface mount.
 */

import { useEffect, useRef } from "react";

import { captureEvent } from "./capture";
import type { Event } from "./registry";

/**
 * Fire ``builder()`` and pass the result to ``captureEvent`` exactly
 * once per component mount. Subsequent invocations of the underlying
 * effect (e.g. React Strict Mode's double-invoke) are no-ops.
 */
export function useCaptureOnce<E extends Event>(builder: () => E): void {
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    captureEvent(builder());
    // The builder is captured by reference; we deliberately don't
    // include it in the dependency array because (a) the guard would
    // double-fire if a parent recreated the closure, and (b) the
    // caller's intent with this hook is "fire once on mount, period".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
